import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Star, Filter, Calendar, Users, PlusCircle, MinusCircle } from 'lucide-react';
import hotelData from '../../data/hotels.json';
import { useNavigate } from 'react-router-dom';
import '../../styles/hotel/hotels.css'

const Hotels = () => {
  const navigate = useNavigate(); 
  // Hotel listing states
  const [hotels, setHotels] = useState(hotelData.hotels || []);
  const [loading, setLoading] = useState(false);
  
  const handleViewDetails = (hotelId) => {
    navigate(`/hotel/${hotelId}`);
  }
  
  // Search states
  const [searchParams, setSearchParams] = useState({
    location: '',
    dateRange: 'Check-in date — Check-out date',
    dates: { startDate: null, endDate: null },
    guests: { adults: 2, children: 0, rooms: 1 }
  });

  // Calendar states
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState({ startDate: null, endDate: null });
  
  // UI states
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestPicker, setShowGuestPicker] = useState(false);

  // Refs for dropdowns
  const locationRef = useRef(null);
  const datePickerRef = useRef(null);
  const guestPickerRef = useRef(null);

  // Get destinations from JSON
  const destinations = hotelData.destinations || [];

  // Calendar helper functions
  const generateDays = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isDateSelectable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };

  const handleDateClick = (date) => {
    if (!isDateSelectable(date)) return;
    
    if (!selectedDates.startDate || (selectedDates.startDate && selectedDates.endDate)) {
      setSelectedDates({ startDate: date, endDate: null });
    } else {
      if (date < selectedDates.startDate) {
        setSelectedDates({ startDate: date, endDate: selectedDates.startDate });
      } else {
        setSelectedDates({ ...selectedDates, endDate: date });
      }
    }
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCurrentMonth(nextMonth);
  };

  const handlePrevMonth = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    setCurrentMonth(prevMonth);
  };

  // Guest handlers
  const handleGuestChange = (type, operation) => {
    const limits = {
      adults: { min: 1, max: 30 },
      children: { min: 0, max: 10 },
      rooms: { min: 1, max: 30 }
    };

    setSearchParams(prev => {
      const newGuests = { ...prev.guests };
      if (operation === 'increment' && newGuests[type] < limits[type].max) {
        newGuests[type]++;
      } else if (operation === 'decrement' && newGuests[type] > limits[type].min) {
        newGuests[type]--;
      }
      return { ...prev, guests: newGuests };
    });
  };

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    
    const filteredHotels = hotelData.hotels.filter(hotel => {
      if (searchParams.location) {
        const locationMatch = 
          hotel.location.city.toLowerCase().includes(searchParams.location.toLowerCase()) ||
          hotel.location.country.toLowerCase().includes(searchParams.location.toLowerCase());
        if (!locationMatch) return false;
      }
      return true;
    });

    setHotels(filteredHotels);
    setLoading(false);
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowLocationDropdown(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
      if (guestPickerRef.current && !guestPickerRef.current.contains(event.target)) {
        setShowGuestPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div>
      {/* Search Section */}
      <div className="bg-[#003580] py-8">
        <div className="container">
          <div className="col-12 col-lg-10 mx-auto">
            <div className="p-4 rounded mt-4" style={{ backgroundColor: 'rgb(15, 118, 101)', important: 'true' }}>
              <form onSubmit={handleSearch} className="row g-2">
                {/* Location Search */}
                <div className="col-12 col-md-3 position-relative" ref={locationRef}>
                  <div 
                    className="bg-white rounded p-2 h-100 d-flex align-items-center cursor-pointer"
                    onClick={() => setShowLocationDropdown(true)}
                  >
                    <MapPin className="text-muted me-2" size={20} />
                    <input
                      type="text"
                      placeholder="Where are you going?"
                      className="form-control border-0 p-0"
                      value={searchParams.location}
                      onChange={(e) => setSearchParams(prev => ({
                        ...prev,
                        location: e.target.value
                      }))}
                    />
                  </div>

                  {showLocationDropdown && destinations.length > 0 && (
                    <div className="position-absolute top-100 start-0 end-0 mt-1 bg-white rounded shadow-lg z-3">
                      <div className="p-3 border-bottom">
                        <h6 className="mb-0">Popular Destinations</h6>
                      </div>
                      {destinations.map((dest) => (
                        <div
                          key={dest.id}
                          className="p-3 hover-bg-light cursor-pointer d-flex align-items-center"
                          onClick={() => {
                            setSearchParams(prev => ({
                              ...prev,
                              location: `${dest.city}, ${dest.country}`
                            }));
                            setShowLocationDropdown(false);
                          }}
                        >
                          <MapPin size={20} className="text-muted me-2" />
                          <div>
                            <div className="fw-medium">{dest.city}</div>
                            <small className="text-muted">{dest.country}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Date Picker */}
                <div className="col-12 col-md-3 position-relative" ref={datePickerRef}>
                  <div 
                    className="bg-white rounded p-2 h-100 d-flex align-items-center cursor-pointer"
                    onClick={() => setShowDatePicker(!showDatePicker)}
                  >
                    <Calendar className="text-muted me-2" size={20} />
                    <span className="text-muted">
                      {selectedDates.startDate && selectedDates.endDate 
                        ? `${formatDate(selectedDates.startDate)} - ${formatDate(selectedDates.endDate)}`
                        : 'Check-in — Check-out'
                      }
                    </span>
                  </div>

                  {showDatePicker && (
                    <div className="position-absolute top-100 start-0 mt-1 bg-white rounded shadow-lg z-3 p-4" 
                         style={{ width: window.innerWidth < 768 ? '300px' : '600px' }}>
                      <div className="d-flex flex-column flex-md-row gap-4">
                        {/* Current Month */}
                        <div className="flex-1">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <button 
                              className="btn btn-sm btn-outline-secondary"
                              onClick={handlePrevMonth}
                              disabled={currentMonth.getMonth() === new Date().getMonth()}
                            >
                              ←
                            </button>
                            <span className="fw-medium">
                              {currentMonth.toLocaleDateString('default', { month: 'long', year: 'numeric' })}
                            </span>
                            <button 
                              className="btn btn-sm btn-outline-secondary"
                              onClick={handleNextMonth}
                            >
                              →
                            </button>
                          </div>
                          <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                              <div key={day} className="text-center text-muted small py-1">{day}</div>
                            ))}
                            {generateDays(currentMonth.getFullYear(), currentMonth.getMonth()).map((date, i) => (
                              <button
                                key={i}
                                type="button"
                                className={`
                                  btn btn-sm p-1 ${!date ? 'invisible' : ''}
                                  ${date && !isDateSelectable(date) ? 'text-muted' : ''}
                                  ${date && selectedDates.startDate?.getTime() === date.getTime() ? 'btn-primary' : ''}
                                  ${date && selectedDates.endDate?.getTime() === date.getTime() ? 'btn-primary' : ''}
                                  ${date && selectedDates.startDate && selectedDates.endDate && 
                                    date > selectedDates.startDate && date < selectedDates.endDate ? 'bg-light' : ''}
                                `}
                                disabled={date && !isDateSelectable(date)}
                                onClick={() => date && handleDateClick(date)}
                              >
                                {date ? date.getDate() : ''}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Next Month - Only show on larger screens */}
                        <div className="flex-1 d-none d-md-block">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="fw-medium">
                              {new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
                                .toLocaleDateString('default', { month: 'long', year: 'numeric' })}
                            </span>
                          </div>
                          <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                              <div key={day} className="text-center text-muted small py-1">{day}</div>
                            ))}
                            {generateDays(
                              currentMonth.getMonth() === 11 ? currentMonth.getFullYear() + 1 : currentMonth.getFullYear(),
                              currentMonth.getMonth() === 11 ? 0 : currentMonth.getMonth() + 1
                            ).map((date, i) => (
                              <button
                                key={i}
                                type="button"
                                className={`
                                  btn btn-sm p-1 ${!date ? 'invisible' : ''}
                                  ${date && !isDateSelectable(date) ? 'text-muted' : ''}
                                  ${date && selectedDates.startDate?.getTime() === date.getTime() ? 'btn-primary' : ''}
                                  ${date && selectedDates.endDate?.getTime() === date.getTime() ? 'btn-primary' : ''}
                                  ${date && selectedDates.startDate && selectedDates.endDate && 
                                    date > selectedDates.startDate && date < selectedDates.endDate ? 'bg-light' : ''}
                                `}
                                disabled={date && !isDateSelectable(date)}
                                onClick={() => date && handleDateClick(date)}
                              >
                                {date ? date.getDate() : ''}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Apply Button */}
                      <div className="d-flex justify-content-end mt-3">
                        <button 
                          type="button"
                          className="btn btn-warning"
                          disabled={!selectedDates.startDate || !selectedDates.endDate}
                          onClick={() => {
                            setSearchParams(prev => ({
                              ...prev,
                              dates: selectedDates,
                              dateRange: `${formatDate(selectedDates.startDate)} - ${formatDate(selectedDates.endDate)}`
                            }));
                            setShowDatePicker(false);
                          }}
                        >
                          Apply Dates
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Guest Picker */}
                <div className="col-12 col-md-4 position-relative" ref={guestPickerRef}>
                  <div 
                    className="bg-white rounded p-2 h-100 d-flex align-items-center cursor-pointer"
                    onClick={() => setShowGuestPicker(!showGuestPicker)}
                  >
                    <Users className="text-muted me-2" size={20} />
                    <span className="text-muted">
                      {searchParams.guests.adults} adults · {searchParams.guests.children} children · {searchParams.guests.rooms} room
                    </span>
                  </div>

                  {showGuestPicker && (
                    <div className="position-absolute top-100 start-0 mt-1 bg-white rounded shadow-lg z-3 p-4" 
                         style={{ width: '300px' }}>
                      {/* Adults */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <div className="fw-medium">Adults</div>
                          <small className="text-muted">Age 13+</small>
                        </div>
                        <div className="d-flex align-items-center gap-4">
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-circle p-1"
                            onClick={() => handleGuestChange('adults', 'decrement')}
                            disabled={searchParams.guests.adults <= 1}
                          >
                            <MinusCircle size={20} />
                          </button>
                          <span className="mx-2">{searchParams.guests.adults}</span>
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-circle p-1"
                            onClick={() => handleGuestChange('adults', 'increment')}
                          >
                            <PlusCircle size={20} />
                          </button>
                        </div>
                      </div>

                      {/* Children */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <div className="fw-medium">Children</div>
                          <small className="text-muted">Ages 0-12</small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-circle p-1"
                            onClick={() => handleGuestChange('children', 'decrement')}
                            disabled={searchParams.guests.children <= 0}
                          >
                            <MinusCircle size={20} />
                          </button>
                          <span className="mx-2">{searchParams.guests.children}</span>
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-circle p-1"
                            onClick={() => handleGuestChange('children', 'increment')}
                          >
                            <PlusCircle size={20} />
                          </button>
                        </div>
                      </div>

                      {/* Rooms */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="fw-medium">Rooms</div>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-circle p-1"
                            onClick={() => handleGuestChange('rooms', 'decrement')}
                            disabled={searchParams.guests.rooms <= 1}
                          >
                            <MinusCircle size={20} />
                          </button>
                          <span className="mx-2">{searchParams.guests.rooms}</span>
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-circle p-1"
                            onClick={() => handleGuestChange('rooms', 'increment')}
                          >
                            <PlusCircle size={20} />
                          </button>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary w-100 mt-3"
                        onClick={() => setShowGuestPicker(false)}
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>

                {/* Search Button */}
                <div className="col-12 col-md-2">
                  <button 
                    type="submit"
                    className="btn btn-warning w-100 h-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      if (navigate) {
                        navigate('/filter', { state: { filteredHotels: hotels, searchParams } });
                      } else {
                        window.location.href = '/filter';
                      }
                    }}
                  >
                    <Search size={20} />
                    <span>{loading ? 'Searching...' : 'Search'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Hotel Listings Section */}
      <div className="container py-4" style={{ position: 'unset' }}>
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <h2 className="h4 mb-0">Available Hotels</h2>
              <div className="d-flex align-items-center gap-3 mt-2 mt-sm-0">
                <span className="text-muted">{hotels.length} properties found</span>
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2">
                  <Filter size={18} />
                  <span>Filters</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {hotels.map(hotel => (
            <div key={hotel.id} className="col-12 col-sm-6 col-lg-3">
              <div className="card h-100 border">
                <div 
                  className="position-relative cursor-pointer"
                  onClick={() => handleViewDetails(hotel.id)}
                  style={{ cursor: "pointer" }}
                >
                  <img 
                    src={hotel.image}
                    alt={hotel.name}
                    className="card-img-top"
                    style={{ height: '200px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = "";
                    }}
                  />
                  {hotel.discount && (
                    <div className="position-absolute top-0 end-0 m-3 badge bg-danger">
                      {hotel.discount}
                    </div>
                  )}
                  {hotel.halal_status && (
                    <div className="position-absolute bottom-0 start-0 m-3 badge bg-success">
                      {hotel.halal_status}
                    </div>
                  )}
                </div>
                <div 
                  className="card-body cursor-pointer" 
                  onClick={() => handleViewDetails(hotel.id)}
                  style={{ cursor: "pointer" }}
                >
                  <h5 className="card-title mb-1">{hotel.name}</h5>
                  <div className="d-flex align-items-center text-muted mb-2">
                    <MapPin size={16} className="me-1" />
                    <small>{`${hotel.location.city}, ${hotel.location.country}`}</small>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="text-warning">
                      {[...Array(hotel.stars)].map((_, i) => (
                        <Star key={i} size={16} fill="currentColor" />
                      ))}
                    </div>
                    <small className="text-muted">({hotel.reviews.count} reviews)</small>
                  </div>
                  <div className="text-muted small mb-3">{hotel.description}</div>
                  <div className="mb-3">
                    {hotel.amenities.slice(0, 3).map((amenity) => (
                      <span key={amenity} className="badge bg-light text-dark me-1 mb-1">
                        {amenity}
                      </span>
                    ))}
                    {hotel.amenities.length > 3 && (
                      <span className="badge bg-secondary">
                        +{hotel.amenities.length - 3}
                      </span>
                    )}
                  </div>
                  {hotel.tags && (
                    <div className="mb-3">
                      {hotel.tags.map((tag) => (
                        <span key={tag} className="badge bg-info bg-opacity-10 text-info me-1 mb-1">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="card-footer bg-white border-top pt-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <small className="text-muted d-block">Per night</small>
                      <div className="d-flex align-items-center gap-2">
                        <span className="h5 mb-0">${hotel.price}</span>
                        <span className="text-muted small">{hotel.currency}</span>
                      </div>
                    </div>

                    <button 
                      className="btn hotel-view-button"
                      onClick={() => handleViewDetails(hotel.id)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {hotels.length === 0 && (
          <div className="text-center py-5">
            <div className="mb-3">
              <Search size={48} className="text-muted" />
            </div>
            <h3>No hotels found matching your criteria</h3>
            <p className="text-muted">Try adjusting your filters for more results</p>
            <button className="btn btn-primary">
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hotels;